/* @img-migrated */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Send, ArrowLeft, MessageCircle, Search, CheckCheck, Sparkles } from 'lucide-react';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const avatarColors = [
  'from-[#E63946] to-[#C62D3A]',
  'from-[#457B9D] to-[#1D3557]',
  'from-[#F4A261] to-[#E76F51]',
  'from-[#2A9D8F] to-[#264653]',
  'from-[#7209B7] to-[#3A0CA3]',
  'from-[#EE6C4D] to-[#3D405B]',
];
const getAvatarColor = (id) =>
  avatarColors[(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % avatarColors.length];

// Global source-of-truth: ONLY user.picture (personal avatar), never company logo_url.
// Matches the profile/posts/comments chain. Initials fallback when absent.
const Avatar = ({ user, size = 40, ring = false }) => {
  const pic = user?.picture;
  const sz = `${size}px`;
  const fontSize = size >= 56 ? 'text-xl' : size >= 44 ? 'text-base' : 'text-sm';
  const style = { width: sz, height: sz };
  const ringClass = ring ? 'ring-2 ring-white shadow-md' : '';
  if (pic) {
    return (
      <Img variant="avatar"
        src={resolveImg(pic)}
        alt={user?.name || ''}
        style={style}
        className={`rounded-full object-cover bg-slate-100 ${ringClass}`}
        data-testid="dm-avatar-img"
      />
    );
  }
  return (
    <div
      style={style}
      className={`rounded-full bg-gradient-to-br ${getAvatarColor(user?.id)} flex items-center justify-center text-white font-semibold ${fontSize} ${ringClass}`}
      data-testid="dm-avatar-initials"
    >
      {user?.name?.charAt(0).toUpperCase() || '?'}
    </div>
  );
};

const getTimeShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 86400) return d.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' });
  if (diff < 604800) return d.toLocaleDateString('en', { weekday: 'short' });
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
};

const formatBubbleTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' });
};

const getDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const msPerDay = 86400000;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const delta = startOfToday - msgDay;
  if (delta === 0) return 'Today';
  if (delta === msPerDay) return 'Yesterday';
  if (delta < 7 * msPerDay && delta > 0) return d.toLocaleDateString('en', { weekday: 'long' });
  return d.toLocaleDateString('en', {
    month: 'long',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// Group consecutive messages from the same sender (within 5 min gap)
const groupMessages = (messages, currentUserId) => {
  const groups = [];
  let lastDateLabel = null;
  messages.forEach((msg) => {
    const dateLabel = getDateLabel(msg.created_at);
    if (dateLabel !== lastDateLabel) {
      groups.push({ type: 'divider', id: `div-${dateLabel}-${msg.id}`, label: dateLabel });
      lastDateLabel = dateLabel;
    }
    const isMine = msg.sender_id === currentUserId;
    const prev = groups[groups.length - 1];
    const canGroup =
      prev &&
      prev.type === 'group' &&
      prev.isMine === isMine &&
      new Date(msg.created_at) - new Date(prev.messages[prev.messages.length - 1].created_at) <
        5 * 60 * 1000;
    if (canGroup) {
      prev.messages.push(msg);
    } else {
      groups.push({
        type: 'group',
        id: `grp-${msg.id}`,
        isMine,
        messages: [msg],
      });
    }
  });
  return groups;
};

const PortalMessages = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);
  const composerRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    return () => clearInterval(pollRef.current);
  }, []);  

  useEffect(() => {
    const recipientId = searchParams.get('to');
    if (recipientId && user) startConversation(recipientId);
  }, [searchParams, user]);  

  useEffect(() => {
    if (activeConvo) {
      fetchMessages(activeConvo.id);
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(activeConvo.id), 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [activeConvo?.id]);  

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/portal/conversations`, { withCredentials: true });
      setConversations(res.data);
    } catch {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async (recipientId) => {
    try {
      const fd = new FormData();
      fd.append('recipient_id', recipientId);
      const res = await axios.post(`${API}/portal/conversations`, fd, { withCredentials: true });
      setActiveConvo(res.data);
      fetchConversations();
    } catch {
      toast.error('Could not start conversation');
    }
  };

  const fetchMessages = async (convoId) => {
    try {
      const res = await axios.get(`${API}/portal/conversations/${convoId}/messages`, {
        withCredentials: true,
      });
      setMessages(res.data);
    } catch {
      /* polling - silent */
    }
  };

  const handleSend = async () => {
    const content = newMsg.trim();
    if (!content || !activeConvo) return;
    setSending(true);
    // Optimistic append
    const optimistic = {
      id: `tmp-${Date.now()}`,
      sender_id: user?.id,
      content,
      read: false,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    setNewMsg('');
    try {
      await axios.post(
        `${API}/portal/conversations/${activeConvo.id}/messages`,
        { content },
        { withCredentials: true }
      );
      fetchMessages(activeConvo.id);
      fetchConversations();
    } catch {
      toast.error('Failed to send');
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setNewMsg(content);
    } finally {
      setSending(false);
    }
  };

  const showList = !activeConvo;
  const filteredConvos = useMemo(
    () =>
      search
        ? conversations.filter((c) =>
            (c.other_user?.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (c.other_user?.company || '').toLowerCase().includes(search.toLowerCase())
          )
        : conversations,
    [conversations, search]
  );

  const grouped = useMemo(() => groupMessages(messages, user?.id), [messages, user?.id]);

  return (
    <div
      className="flex overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100"
      style={{ height: 'calc(100dvh - 64px)' }}
      data-testid="portal-messages"
    >
      {/* ===== INBOX / CONVERSATION LIST ===== */}
      <aside
        className={`${showList ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-[340px] lg:w-[380px] bg-white/80 backdrop-blur-sm border-r border-slate-200/70 flex-shrink-0`}
        data-testid="dm-inbox"
      >
        {/* Inbox header */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-200/70">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-[22px] font-bold text-[#002249] tracking-tight">Messages</h2>
            <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
              {conversations.length} {conversations.length === 1 ? 'chat' : 'chats'}
            </span>
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations"
              className="w-full h-10 pl-9 pr-3 bg-slate-100/70 hover:bg-slate-100 focus:bg-white focus:ring-2 focus:ring-[#002249]/15 focus:border-[#002249]/30 border border-transparent rounded-xl text-[16px] text-slate-800 placeholder:text-slate-400 transition-all outline-none"
              data-testid="dm-search-input"
            />
          </div>
        </div>

        {/* Convo scroll area */}
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-6 h-6 border-[3px] border-[#002249] border-t-transparent rounded-full" />
            </div>
          ) : filteredConvos.length > 0 ? (
            <ul className="px-2">
              {filteredConvos.map((convo) => {
                const active = activeConvo?.id === convo.id;
                const unread = convo.unread > 0;
                return (
                  <li key={convo.id}>
                    <button
                      onClick={() => setActiveConvo(convo)}
                      className={`group w-full flex items-center gap-3 px-3 py-3 my-0.5 rounded-xl text-left transition-all duration-200
                        ${active
                          ? 'bg-gradient-to-r from-[#002249]/8 to-[#002249]/4 ring-1 ring-[#002249]/15 shadow-sm'
                          : 'hover:bg-slate-100/80'
                        }`}
                      data-testid={`conversation-${convo.id}`}
                    >
                      <div className="relative flex-shrink-0">
                        <Avatar user={convo.other_user} size={48} />
                        {unread && (
                          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-[#E63946] ring-2 ring-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p
                            className={`text-[14.5px] truncate ${unread ? 'font-bold text-[#050505]' : 'font-semibold text-slate-800'}`}
                          >
                            {convo.other_user?.name || 'Member'}
                          </p>
                          <span
                            className={`text-[11px] flex-shrink-0 ${unread ? 'text-[#E63946] font-semibold' : 'text-slate-400'}`}
                          >
                            {getTimeShort(convo.updated_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <p
                            className={`text-[13px] truncate flex-1 ${unread ? 'text-slate-800 font-medium' : 'text-slate-500'}`}
                          >
                            {convo.last_message || (
                              <span className="italic text-slate-400">
                                Say hello&nbsp;
                                <Sparkles className="inline w-3 h-3 -mt-0.5" />
                              </span>
                            )}
                          </p>
                          {unread && (
                            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#E63946] text-white text-[10.5px] font-bold flex items-center justify-center flex-shrink-0">
                              {convo.unread}
                            </span>
                          )}
                        </div>
                        {convo.other_user?.company && (
                          <p className="text-[11.5px] text-slate-400 truncate mt-0.5">
                            {convo.other_user.company}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex flex-col items-center justify-center text-center px-8 py-16">
              <div className="relative w-20 h-20 mb-5">
                <div className="absolute inset-0 rounded-full bg-[#002249]/5 animate-pulse" />
                <div className="absolute inset-2 rounded-full bg-[#002249]/8 flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-[#002249]/60" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-[15px] font-semibold text-slate-800 mb-1.5">
                {search ? 'No matches found' : 'Your inbox is empty'}
              </p>
              <p className="text-[12.5px] text-slate-500 leading-relaxed max-w-[240px]">
                {search
                  ? 'Try a different name or company.'
                  : 'Visit a member\'s profile and tap Message to start a conversation.'}
              </p>
            </div>
          )}
        </div>
      </aside>

      {/* ===== CHAT PANEL ===== */}
      <section
        className={`${!showList ? 'flex' : 'hidden'} md:flex flex-col flex-1 min-w-0 relative`}
        data-testid="dm-chat-panel"
      >
        {activeConvo ? (
          <>
            {/* Chat header */}
            <header className="flex items-center gap-3 px-4 md:px-6 h-[68px] bg-white/85 backdrop-blur-md border-b border-slate-200/70 flex-shrink-0">
              <button
                onClick={() => setActiveConvo(null)}
                className="md:hidden w-9 h-9 -ml-1 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-600 transition-colors"
                data-testid="dm-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Link
                to={`/portal/members/${activeConvo.other_user?.id}`}
                className="flex-shrink-0"
                data-testid="dm-header-avatar-link"
              >
                <Avatar user={activeConvo.other_user} size={44} ring />
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/portal/members/${activeConvo.other_user?.id}`}
                  className="text-[15px] font-semibold text-[#050505] hover:text-[#002249] truncate block leading-tight"
                  data-testid="dm-header-name"
                >
                  {activeConvo.other_user?.name || 'Member'}
                </Link>
                <p className="text-[12px] text-slate-500 truncate leading-tight mt-0.5">
                  {[activeConvo.other_user?.title, activeConvo.other_user?.company]
                    .filter(Boolean)
                    .join(' · ') || 'DCCF Member'}
                </p>
              </div>
            </header>

            {/* Messages stream */}
            <div
              className="flex-1 overflow-y-auto px-3 md:px-6 py-5 md:py-6"
              data-testid="message-list"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 0%, rgba(0,34,73,0.04), transparent 60%), radial-gradient(circle at 80% 100%, rgba(230,57,70,0.04), transparent 60%)',
              }}
            >
              <div className="max-w-[760px] mx-auto space-y-1">
                {grouped.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#002249]/10 to-[#002249]/5 flex items-center justify-center mb-4">
                      <Avatar user={activeConvo.other_user} size={44} />
                    </div>
                    <p className="text-[15px] font-semibold text-slate-800 mb-1">
                      You and {activeConvo.other_user?.name?.split(' ')[0] || 'Member'} are now connected
                    </p>
                    <p className="text-[13px] text-slate-500">
                      Send the first message to get the conversation started.
                    </p>
                  </div>
                ) : (
                  grouped.map((block, idx) => {
                    if (block.type === 'divider') {
                      return (
                        <div
                          key={block.id}
                          className="flex items-center gap-3 py-3 px-1"
                          data-testid="dm-date-divider"
                        >
                          <div className="h-px flex-1 bg-slate-200" />
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 bg-white/70 px-3 py-1 rounded-full border border-slate-200/60">
                            {block.label}
                          </span>
                          <div className="h-px flex-1 bg-slate-200" />
                        </div>
                      );
                    }
                    const { isMine, messages: grpMsgs } = block;
                    return (
                      <div
                        key={block.id}
                        className={`flex gap-2.5 mb-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                      >
                        {/* Other user's avatar on the left, only on group block */}
                        {!isMine && (
                          <div className="flex-shrink-0 self-end mb-1">
                            <Avatar user={activeConvo.other_user} size={32} />
                          </div>
                        )}
                        <div
                          className={`flex flex-col gap-1 max-w-[78%] sm:max-w-[65%] ${isMine ? 'items-end' : 'items-start'}`}
                        >
                          {grpMsgs.map((msg, i) => {
                            const isLast = i === grpMsgs.length - 1;
                            const isFirst = i === 0;
                            const baseRadius = 'rounded-2xl';
                            const cornerFix = isMine
                              ? isLast
                                ? 'rounded-br-md'
                                : ''
                              : isLast
                                ? 'rounded-bl-md'
                                : '';
                            return (
                              <div
                                key={msg.id}
                                className={`relative ${baseRadius} ${cornerFix} px-3.5 py-2.5 text-[14.5px] leading-[1.45] shadow-sm transition-all
                                  ${isMine
                                    ? 'bg-gradient-to-br from-[#002249] to-[#001630] text-white'
                                    : 'bg-white text-[#0d0d0d] border border-slate-100'
                                  }
                                  ${msg._pending ? 'opacity-70' : 'opacity-100'}`}
                                data-testid={`message-bubble-${idx}-${i}`}
                              >
                                <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                {isLast && (
                                  <div
                                    className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}
                                  >
                                    <span
                                      className={`text-[10.5px] ${isMine ? 'text-white/60' : 'text-slate-400'}`}
                                    >
                                      {formatBubbleTime(msg.created_at)}
                                    </span>
                                    {isMine && !msg._pending && (
                                      <CheckCheck
                                        className={`w-3.5 h-3.5 ${msg.read ? 'text-sky-300' : 'text-white/45'}`}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Composer */}
            <div
              ref={composerRef}
              className="flex-shrink-0 bg-white/90 backdrop-blur-md border-t border-slate-200/70 px-3 md:px-6 py-3 safe-area-bottom"
            >
              <div className="max-w-[760px] mx-auto flex items-end gap-2.5">
                <div className="flex-1 relative">
                  <textarea
                    rows={1}
                    value={newMsg}
                    onChange={(e) => {
                      setNewMsg(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Write a message…"
                    className="w-full min-h-[48px] max-h-[120px] px-4 py-3 bg-slate-100/80 hover:bg-slate-100 focus:bg-white focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]/30 border border-transparent rounded-2xl text-[16px] text-slate-900 placeholder:text-slate-400 resize-none outline-none transition-all"
                    data-testid="message-input"
                  />
                </div>
                <motion.button
                  onClick={handleSend}
                  disabled={sending || !newMsg.trim()}
                  whileTap={{ scale: 0.92 }}
                  whileHover={{ scale: newMsg.trim() ? 1.04 : 1 }}
                  className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-sm
                    ${newMsg.trim() && !sending
                      ? 'bg-gradient-to-br from-[#002249] to-[#001630] hover:shadow-lg text-white'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  data-testid="send-message-btn"
                  aria-label="Send message"
                >
                  <Send className="w-[18px] h-[18px]" />
                </motion.button>
              </div>
            </div>
          </>
        ) : (
          <EmptyChatState />
        )}
      </section>
    </div>
  );
};

const EmptyChatState = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-12">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="relative mb-6"
    >
      <div className="absolute inset-0 rounded-full bg-[#002249]/6 blur-2xl scale-150" />
      <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-white to-slate-50 border border-slate-200 shadow-sm flex items-center justify-center">
        <MessageCircle className="w-12 h-12 text-[#002249]" strokeWidth={1.5} />
      </div>
    </motion.div>
    <h3 className="text-xl font-bold text-[#002249] mb-2 tracking-tight">Your messages</h3>
    <p className="text-[14px] text-slate-500 leading-relaxed max-w-[320px]">
      Pick a conversation from the left, or start a new one by visiting a member&apos;s profile.
    </p>
    <div className="mt-5 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#002249]/5 text-[12px] font-medium text-[#002249]">
      <Sparkles className="w-3.5 h-3.5" />
      Private · End-to-end inside DCCF
    </div>
  </div>
);

export default PortalMessages;
